import { Link, NavLink, useNavigate } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { useLang } from "../contexts/LanguageContext";
import { Button } from "./ui/button";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
  DropdownMenuLabel, DropdownMenuSeparator,
} from "./ui/dropdown-menu";
import { Globe, LayoutDashboard, BookOpen, Target, Timer, Sparkles, LogOut, Menu, Grid3X3, User } from "lucide-react";
import { useState } from "react";

const LANGS = [
  { code: "en", label: "English" },
  { code: "so", label: "Soomaali" },
  { code: "ar", label: "العربية" },
];

export default function Navbar() {
  const { user, logout } = useAuth();
  const { lang, setLang, t } = useLang();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);

  const navLink = "px-3 py-2 text-sm font-medium rounded-md transition-colors";
  const activeCls = "bg-fjord/10 text-fjord";
  const idleCls = "text-gray-700 hover:bg-gray-100";

  return (
    <header className="sticky top-0 z-40 bg-white/85 backdrop-blur-md border-b border-gray-200">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between">
        <Link to={user ? "/dashboard" : "/"} className="flex items-center gap-2" data-testid="navbar-logo">
          <div className="w-8 h-8 rounded-md bg-fjord text-white grid place-items-center font-bold font-heading">V</div>
          <span className="font-heading font-bold text-lg tracking-tight">VeiKlar</span>
        </Link>

        {user && (
          <nav className="hidden md:flex items-center gap-1">
            <NavLink to="/dashboard" data-testid="nav-dashboard" className={({isActive})=>`${navLink} ${isActive?activeCls:idleCls}`}>
              <LayoutDashboard className="inline-block w-4 h-4 me-1.5"/>{t("dashboard")}
            </NavLink>
            <NavLink to="/learn" data-testid="nav-learn" className={({isActive})=>`${navLink} ${isActive?activeCls:idleCls}`}>
              <BookOpen className="inline-block w-4 h-4 me-1.5"/>{t("learn")}
            </NavLink>
            <NavLink to="/practice" data-testid="nav-practice" className={({isActive})=>`${navLink} ${isActive?activeCls:idleCls}`}>
              <Target className="inline-block w-4 h-4 me-1.5"/>{t("practice")}
            </NavLink>
            <NavLink to="/mock" data-testid="nav-mock" className={({isActive})=>`${navLink} ${isActive?activeCls:idleCls}`}>
              <Timer className="inline-block w-4 h-4 me-1.5"/>{t("mockExam")}
            </NavLink>
            <NavLink to="/signs" data-testid="nav-signs" className={({isActive})=>`${navLink} ${isActive?activeCls:idleCls}`}>
              <Sparkles className="inline-block w-4 h-4 me-1.5"/>{t("signs")}
            </NavLink>
            <NavLink to="/markings" data-testid="nav-markings" className={({isActive})=>`${navLink} ${isActive?activeCls:idleCls}`}>
              <Sparkles className="inline-block w-4 h-4 me-1.5"/>{t("markings")}
            </NavLink>
          </nav>
        )}

        <div className="flex items-center gap-2">
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline" size="sm" data-testid="language-switcher" className="gap-1.5">
                <Globe className="w-4 h-4" />
                <span className="hidden sm:inline">{LANGS.find(l=>l.code===lang)?.label}</span>
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuLabel>{t("language")}</DropdownMenuLabel>
              <DropdownMenuSeparator/>
              {LANGS.map(l => (
                <DropdownMenuItem key={l.code} data-testid={`lang-option-${l.code}`} onClick={() => setLang(l.code)}>
                  {l.label} {lang===l.code && "✓"}
                </DropdownMenuItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>

          {user ? (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button data-testid="user-menu-trigger" className="flex items-center gap-2 px-2 py-1 rounded-md hover:bg-gray-100">
                  {user.picture ? (
                    <img src={user.picture} alt={user.name} className="w-7 h-7 rounded-full" />
                  ) : (
                    <div className="w-7 h-7 rounded-full bg-fjord text-white grid place-items-center text-sm">
                      {user.name?.[0] || "U"}
                    </div>
                  )}
                  <span className="hidden sm:inline text-sm font-medium">{user.name?.split(" ")[0]}</span>
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuLabel className="text-xs text-gray-500">{user.email}</DropdownMenuLabel>
                <DropdownMenuSeparator/>
                <DropdownMenuItem onClick={() => navigate("/account")}>
                  <User className="w-4 h-4 me-2"/>Min konto
                </DropdownMenuItem>
                <DropdownMenuItem data-testid="nav-coverage" onClick={() => navigate("/coverage")}>
                  <Grid3X3 className="w-4 h-4 me-2"/>{t("coverageTitle")}
                </DropdownMenuItem>
                <DropdownMenuSeparator/>
                <DropdownMenuItem data-testid="logout-button" onClick={logout}>
                  <LogOut className="w-4 h-4 me-2"/>{t("signOut")}
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          ) : null}

          {user && (
            <button data-testid="mobile-menu-toggle" className="md:hidden p-2 rounded hover:bg-gray-100" onClick={()=>setOpen(o=>!o)}>
              <Menu className="w-5 h-5" />
            </button>
          )}
        </div>
      </div>
      {user && open && (
        <div className="md:hidden border-t border-gray-200 bg-white px-4 py-3 space-y-1">
          {[
            ["/dashboard", t("dashboard")], ["/learn", t("learn")], ["/practice", t("practice")],
            ["/mock", t("mockExam")], ["/signs", t("signs")], ["/markings", t("markings")],
          ].map(([to, label]) => (
            <button key={to} data-testid={`mobile-nav-${to.replace("/","")}`}
              className="block w-full text-start px-3 py-2 rounded hover:bg-gray-100"
              onClick={()=>{ setOpen(false); navigate(to); }}>{label}</button>
          ))}
        </div>
      )}
    </header>
  );
}
